import {
  Users, TrendingUp, Percent, Package, DollarSign, Megaphone, Receipt, Award, Link2,
} from "lucide-react";
import ScorecardInfoTip from "./ScorecardInfoTip";
import { fmtNum, fmtMoney, fmtMoneyShort } from "./formatters";

/**
 * Executive summary ribbon — always shows global metrics for the filtered
 * period (no channel drill-down, so the exec view stays stable while the
 * user probes campaigns / channels below).
 */
export default function OverviewRibbon({ o }) {
  if (!o) return null;
  const items = [
    {
      key: "leads", icon: Users, color: "#0ea5e9",
      label: "Leads Digitales",
      value: fmtNum(o.total_leads),
      sub:   `${fmtNum(o.avg_leads_per_month || 0)} / mes en promedio`,
      tip:   "count(leads_digital) donde created_at ∈ período — filtros de marca/modelo/sucursal/unidad aplicados.",
      extra: `Promedio calculado sobre ${o.active_months || 0} mes(es) con actividad.`,
      src:   "leads_digital (Zapier · pixel Meta/Google)",
    },
    {
      key: "l2s", icon: Percent, color: "#8b5cf6",
      label: "Lead → Venta",
      value: `${(o.lead_to_sale_pct || 0).toFixed(2)}%`,
      sub:   `${fmtNum(o.digital_units_matched)} unidades / ${fmtNum(o.total_leads)} leads`,
      tip:   "unidades digitales vendidas ÷ leads totales × 100",
      extra: "Base de conversión = unidades vendidas atribuidas a leads (233), NO lead-events. Evita doble-conteo cuando un cliente envía múltiples formularios antes de comprar.",
      src:   "sales ∩ leads_digital (matched · ventana anual 30-90 días)",
    },
    {
      // Tasa de Calificación (Gabriela Sosa · Scorecard v3 · 2026-02-16).
      // Meta: ≥ 10% → verde. Cálculo alineado con la línea azul del
      // timeseries y con la invariante del funnel (%Calif ≥ %L2S).
      key: "tasa-calif", icon: Award,
      color: (o.qualification_rate_digital || 0) >= 10 ? "#16a34a"
           : (o.qualification_rate_digital || 0) >= 5  ? "#f97316" : "#dc2626",
      label: "Tasa de Calificación",
      value: `${(o.qualification_rate_digital || 0).toFixed(2)}%`,
      sub:   `${fmtNum(o.qualified_digital_count || 0)} calificados / ${fmtNum(o.identified_digital_count || o.total_leads)} identificados`,
      tip:   "leads digitales identificados en CRM que fueron Calificados (o cerraron a venta) ÷ leads identificados en CRM × 100.",
      extra: "KPI Scorecard 2026 (Gabriela Sosa · 30%). Meta ≥10% (verde) · 5-10% amarillo · <5% rojo. Denominador: leads digitales que llegaron al CRM (excluye pixel drops). Numerador: los que fueron marcados Calificado O cerraron a venta (el CRM no tiene estado 'Ganado'). Respeta la invariante del funnel: %Calif ≥ %L2S siempre.",
      src:   "leads_raw(status=Calificado ∪ sale-matched) ∩ leads_digital(identified)",
      breakdown: (o.qualification_rate_by_unit || []).map((row) => ({
        label: row.unit,
        value: `${row.qualification_rate.toFixed(2)}%`,
        sub:   `${fmtNum(row.qualified_count)}/${fmtNum(row.identified_count)}`,
      })),
    },
    {
      // Lead Tracking Integrity Score (Romantiezer Cid · 25%). PROXY basado
      // en % de leads_digital con match compuesto en leads_raw. Meta ≥ 95%.
      // Refinamiento futuro: incluir showroom-assignment accuracy vía Zapier API.
      key: "integrity", icon: Link2,
      color: (o.lead_tracking_integrity_pct || 0) >= 95 ? "#16a34a"
           : (o.lead_tracking_integrity_pct || 0) >= 80 ? "#f97316" : "#dc2626",
      label: "Tracking Integrity",
      value: `${(o.lead_tracking_integrity_pct || 0).toFixed(1)}%`,
      sub:   `${fmtNum(o.tracked_digital_count || 0)} rastreados / ${fmtNum(o.total_leads)} leads`,
      tip:   "% de leads digitales (pixel Meta/Google) que tienen un registro correspondiente en el CRM Dynamics.",
      extra: "KPI Scorecard 2026 (Romantiezer · 25%). Meta ≥95% (verde) · 80-95% amarillo · <80% rojo. Gap = leads capturados por Pixel pero no ingeridos por Zapier al CRM → indica atribución rota. Este es un proxy interno; se refinará cuando integremos la API de Zapier para medir showroom-assignment accuracy.",
      src:   "leads_digital ∩ leads_raw (matching compuesto)",
    },
    {
      key: "contrib", icon: TrendingUp, color: "#22c55e",
      label: "Contribución Digital",
      value: `${(o.digital_contribution_pct || 0).toFixed(1)}%`,
      sub:   `${fmtNum(o.digital_units_matched)} de ${fmtNum(o.total_units_sold)} unid.`,
      tip:   "unidades digitales ÷ unidades totales × 100",
      extra: "Qué % de las ventas del período vinieron de un lead digital (ventana rolling anual).",
      src:   "sales ∩ leads_digital",
    },
    {
      key: "units", icon: Package, color: "#f4c400",
      label: "Unidades Vendidas",
      value: fmtNum(o.total_units_sold),
      sub:   "totales del período",
      tip:   "count(sales) donde sales.date ∈ período — con filtros aplicados.",
      src:   "sales (misma fuente que Dashboard Forecast)",
    },
    {
      key: "digital_units", icon: Package, color: "#3b82f6",
      label: "Unidades Digitales",
      value: fmtNum(o.digital_units_matched),
      sub:   `${fmtNum(o.avg_digital_units_per_month || 0)} / mes · ${(o.digital_contribution_pct || 0).toFixed(1)}% del total`,
      tip:   "Unidades vendidas atribuidas a leads digitales — base histórica para forecast de 6 meses.",
      extra: "Match compuesto contra leads_digital de todo el año (ventana rolling 30-90 días para respetar el ciclo automotriz). Es el KPI a proyectar para metas comerciales.",
      src:   "sales ∩ leads_digital (matched by email/nombre/teléfono)",
    },
    {
      key: "amount", icon: DollarSign, color: "#ef4444",
      label: "Monto Vendido",
      value: fmtMoneyShort(o.total_sales_amount),
      sub:   fmtMoney(o.total_sales_amount),
      tip:   "Σ sales.valor_neto (RD$ netos, ya con descuentos comerciales aplicados).",
      src:   "sales.valor_neto",
    },
    {
      key: "avg_ticket", icon: Receipt, color: "#14b8a6",
      label: "Ticket Promedio",
      value: fmtMoneyShort(o.avg_ticket_total),
      sub:   `Digital: ${fmtMoneyShort(o.avg_ticket_digital)}`,
      tip:   "Σ valor_neto ÷ unidades vendidas (RD$ netos por unidad).",
      extra: "Refleja el precio promedio de venta con filtros aplicados (marca/modelo/sucursal/unidad). El sub-valor 'Digital' aplica el mismo cálculo solo a las unidades atribuidas a leads digitales.",
      src:   "sales.valor_neto ÷ count(sales)",
    },
    {
      key: "campaigns", icon: Megaphone, color: "#a855f7",
      label: "Campañas Activas",
      value: fmtNum(o.campaigns_count),
      sub:   "UTM únicas en el período",
      tip:   "count(DISTINCT leads_digital.campaign) para el período y filtros seleccionados.",
      extra: "Prioriza SIEMPRE el UTM de leads_digital (pixel). El campo campaign del CRM (leads_raw) queda solo para auditoría secundaria.",
      src:   "leads_digital.campaign (UTM · Zapier)",
    },
  ];
  return (
    <div
      className="mb-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3"
      data-testid="overview-ribbon">
      {/*
        Fixed 5-column grid on tablet+ (10 KPIs → 2 clean rows sin huecos).
        Móviles usan 2 columnas (5 filas de 2). Antes usábamos auto-fit
        que resultaba en filas desbalanceadas (8 + 2) en pantallas anchas.
      */}
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.key}
            className="surface p-4 flex items-center gap-3"
            data-testid={`overview-${it.key}`}
            style={{ position: "relative", overflow: "hidden" }}>
            <div className="kpi-icon-clean" style={{
              width: 40, height: 40, color: it.color,
              boxShadow: `inset 0 0 0 1px ${it.color}33`,
            }}>
              <Icon size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <span className="truncate">{it.label}</span>
                <ScorecardInfoTip label={it.label} formula={it.tip} extra={it.extra} source={it.src} />
              </div>
              <div className="text-2xl font-extrabold tabular-nums" style={{
                fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em", color: "#111827",
              }}>
                {it.value}
              </div>
              <div className="text-[10px] text-gray-500 truncate" title={it.sub}>{it.sub}</div>
              {it.breakdown && it.breakdown.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5"
                  data-testid={`overview-${it.key}-breakdown`}>
                  {it.breakdown.map((b) => (
                    <div key={b.label}
                      className="px-2 py-1 rounded-md"
                      style={{
                        background: "#f8fafc", border: "1px solid #e2e8f0",
                        display: "inline-flex", flexDirection: "column",
                        alignItems: "flex-start", lineHeight: 1.05,
                      }}
                      title={`${b.label}: ${b.value} (${b.sub})`}>
                      <span className="text-[8.5px] font-black uppercase"
                        style={{ color: it.color, letterSpacing: "0.06em" }}>
                        {b.label}
                      </span>
                      <span className="text-[11.5px] font-extrabold tabular-nums"
                        style={{ color: "#111827" }}>
                        {b.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
